import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

export interface OnboardingStepperStep {
  label: string;
  complete: boolean;
}

interface OnboardingStepperProps {
  steps: OnboardingStepperStep[];
  className?: string;
}

export function OnboardingStepper({
  steps,
  className,
}: OnboardingStepperProps): JSX.Element {
  let currentIndex = 0;
  for (let i = 0; i < steps.length; i++) {
    if (!steps[i].complete) {
      currentIndex = i;
      break;
    }
    currentIndex = i;
  }

  return (
    <nav
      aria-label="Progress"
      className={cn("w-full", className)}
    >
      {/*
        THE LABEL HANGS UNDER ITS OWN CIRCLE, which is why it is absolutely
        positioned rather than stacked. Centring a label inside an equal-width
        cell puts it under the MIDDLE OF THE CELL, and the circles sit at the
        cell edges — so the first and last labels visibly drifted off their
        own steps. The row reserves `pb-6` for them instead.
      */}
      <ol className="flex items-center pb-0 sm:pb-6">
        {steps.map((step, index) => {
          const isComplete = step.complete;
          const isCurrent = index === currentIndex;
          const isLast = index === steps.length - 1;

          return (
            <li
              key={index}
              className={cn("flex items-center", !isLast && "flex-1")}
            >
              <div className="relative flex flex-col items-center">
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors",
                    isComplete &&
                      "border-primary bg-primary text-primary-foreground",
                    isCurrent &&
                      !isComplete &&
                      "border-primary bg-background text-primary",
                    !isComplete &&
                      !isCurrent &&
                      "border-muted bg-background text-muted-foreground"
                  )}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  {isComplete ? (
                    <Check className="h-4 w-4" aria-hidden />
                  ) : (
                    index + 1
                  )}
                  {/* The label is hidden on a narrow screen, where five or six
                      of them would collide — but it is what names the step, so
                      it stays readable to a screen reader at every width. */}
                  <span className="sr-only">{step.label}</span>
                </div>
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-full mt-2 hidden whitespace-nowrap text-xs font-medium sm:block",
                    isCurrent && "text-foreground",
                    isComplete && "text-primary",
                    !isComplete && !isCurrent && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div
                  className={cn(
                    "mx-2 h-0.5 flex-1 transition-colors",
                    isComplete ? "bg-primary" : "bg-muted"
                  )}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
      {/* Narrow screens get one line instead of five labels on top of each
          other: which step this is, and what it is called. */}
      <p className="mt-2 text-center text-xs font-medium text-foreground sm:hidden">
        {steps[currentIndex]?.label}
      </p>
    </nav>
  );
}
